/**
 * Labstep
 *
 * @module containers/Entity/Create
 * @desc Container for creating entities
 */

import { LayoutLoadingEntityCreate as LoadingLoadingEntityCreate } from 'labstep-web/components/Layout/LoadingEntityCreate';
import { UserGroup } from 'labstep-web/models/user-group.model';
import { getHumanReadableEntityName } from 'labstep-web/services/i18n.service';
import { createEntity } from 'labstep-web/state/actions/entity';
import {
  selectCreatedEntity,
  selectCreateEntityStatus,
} from 'labstep-web/state/selectors/entity';
import * as React from 'react';
import { useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { v4 } from 'uuid';
import type {
  EntityCreateContainerProps,
  IUuidContainerProps,
} from './types';

const Container = ({
  children,
  loadingProps,
  ...rest
}: EntityCreateChildrenProps & {
  loadingProps: {
    entityName: string;
    showLoaderWhileCreating: boolean;
  };
  children: (
    childrenProps: EntityCreateChildrenProps & {
      refreshUuid: VoidFunction;
      uuid: string;
    },
  ) => React.ReactNode;
}) => {
  if (
    rest.status?.isFetching &&
    loadingProps.showLoaderWhileCreating
  ) {
    return (
      <>
        {createPortal(
          <LoadingLoadingEntityCreate
            defaultFetching={rest.status?.isFetching ?? false}
            status={rest.status ?? { isFetching: false }}
            message={
              rest.entityName === UserGroup.entityName
                ? `Joining ${loadingProps.entityName}...`
                : `Creating ${loadingProps.entityName}...`
            }
          >
            <div />
          </LoadingLoadingEntityCreate>,
          document.body,
        )}
      </>
    );
  }
  return children({
    ...rest,
    refreshUuid: rest.refreshUuid,
    uuid: rest.uuid,
  }) as JSX.Element;
};

export interface EntityCreateChildrenProps {
  create: (
    body: any,
    options?: any,
    parentName?: string,
    parentId?: number | string,
  ) => void;
  status: any;
  entity: any;
  entityName: string;
  uuid: string;
  refreshUuid: VoidFunction;
}

const ConnectedChildren = ({
  entityName,
  uuid,
  refreshUuid,
  parentName,
  parentId,
  batch,
  optimistic,
  childKeyName,
  showLoaderWhileCreating,
  isTemplate,
  children,
}: EntityCreateContainerProps<any> & {
  children: (
    childrenProps: EntityCreateChildrenProps & {
      refreshUuid: VoidFunction;
      uuid: string;
    },
  ) => React.ReactNode;
}) => {
  const dispatch = useDispatch();

  const status = useSelector((state: any) =>
    selectCreateEntityStatus(state, entityName, uuid),
  );

  const entity = useSelector((state: any) =>
    selectCreatedEntity(state, entityName, uuid),
  );

  const create = useCallback(
    (
      body: any,
      options: any = {},
      parentNameArg?: string,
      parentIdArg?: number | string,
    ): void => {
      let enhancedOptions = {
        ...options,
        onSuccess: (
          ...args: Parameters<typeof options.onSuccess>
        ) => {
          refreshUuid();
          if (options.onSuccess) {
            options.onSuccess(...args);
          }
        },
      };
      if (batch) {
        enhancedOptions = { ...enhancedOptions, batch: true };
      }

      dispatch(
        createEntity(
          entityName,
          body,
          parentNameArg || parentName,
          parentIdArg || parentId,
          uuid,
          enhancedOptions,
          childKeyName,
          optimistic,
        ),
      );
    },
    [
      dispatch,
      entityName,
      uuid,
      refreshUuid,
      parentName,
      parentId,
      batch,
      optimistic,
      childKeyName,
    ],
  );

  const loadingProps = {
    entityName: getHumanReadableEntityName(
      entityName,
      false,
      false,
      isTemplate,
    ),
    showLoaderWhileCreating: !!showLoaderWhileCreating,
  };

  const childrenProps: EntityCreateChildrenProps = {
    create,
    status,
    entity,
    entityName,
    uuid,
    refreshUuid,
  };

  return (
    <Container
      loadingProps={loadingProps}
      {...childrenProps}
      refreshUuid={refreshUuid}
      uuid={uuid}
    >
      {children}
    </Container>
  );
};

interface IState {
  uuid: string;
}

/**
 * Generate uuid
 */
export class UuidContainer extends React.Component<
  IUuidContainerProps<any> & {
    children: (
      childrenProps: EntityCreateChildrenProps & {
        refreshUuid: VoidFunction;
        uuid: string;
      },
    ) => React.ReactNode;
  },
  IState
> {
  constructor(
    props: IUuidContainerProps<any> & {
      children: (
        childrenProps: EntityCreateChildrenProps & {
          refreshUuid: VoidFunction;
          uuid: string;
        },
      ) => JSX.Element | null;
    },
  ) {
    super(props);
    this.state = {
      uuid: v4(),
    };
    this.refreshUuid = this.refreshUuid.bind(this);
  }

  refreshUuid() {
    this.setState({ uuid: v4() });
  }

  render() {
    const uuid = this.props.uuid || this.state.uuid;

    return (
      <ConnectedChildren
        {...this.props}
        refreshUuid={this.refreshUuid}
        uuid={uuid}
      >
        {this.props.children}
      </ConnectedChildren>
    );
  }
}

export const EntityCreateContainer = UuidContainer;
